import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField, Button, Container, Typography, Paper, Box,
    Collapse, Link, IconButton, Dialog, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import API from '../api/axiosInstance';
import { useAppTheme } from '../themes/ThemeContext';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const { mode } = useAppTheme();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMessage, setModalMessage] = useState('Please wait, we are loading your settings.');
    const [showRetry, setShowRetry] = useState(false);
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setShowRetry(false);
        setModalMessage('Please wait while load your profile.');
        setModalVisible(true);

        try {
            const response = await API.post('/auth/login', { username, password });

            localStorage.setItem('user', JSON.stringify(response.data));
            navigate('/dashboard');
        } catch (err: any) {
            const message = err?.response?.data?.message || 'An unexpected error occurred.';
            setModalMessage(`We were unable to log you in. Reason = ${message}`);
            setShowRetry(true);
        }
    };

    const handleRetry = () => {
        setModalVisible(false);
    };

    return (
        <Box
            className="d-flex flex-column min-vh-100"
            sx={{
                bgcolor: mode === 'dark' ? '#1c1c1c' : '#f5f5f5',
                color: mode === 'dark' ? '#f5f5f5' : '#111',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Header />

            <Container maxWidth="sm" sx={{ mt: 12, mb: 4, flex: 1 }}>
                <Typography variant="h5" align="center" sx={{ mb: 2 }}>
                    Welcome, please login to continue.
                </Typography>

                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h5" align="center" gutterBottom>
                        Login
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Username"
                                variant="outlined"
                                fullWidth
                                required
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                            <TextField
                                label="Password"
                                variant="outlined"
                                type="password"
                                fullWidth
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <Button type="submit" variant="contained" color="primary" fullWidth>
                                Login
                            </Button>

                            <Box textAlign="center">
                                <Link href="/auth/request-password-reset" underline="hover">
                                    &lt;Forgot Login&gt;
                                </Link>
                            </Box>

                            <Box mt={2}>
                                <Typography variant="body2">
                                    By logging in, you are agreeing to...
                                    <IconButton
                                        onClick={() => setDisclaimerOpen(!disclaimerOpen)}
                                        size="small"
                                        aria-label="expand disclaimer"
                                    >
                                        <ExpandMoreIcon />
                                    </IconButton>
                                </Typography>
                                <Collapse in={disclaimerOpen}>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        ..., you are agreeing to allow us to collect all sorts of your data. We promise not to sell or share it at any time, unless required by law. Please see our{' '}
                                        <Link href="/privacy-policy" target="_blank">privacy policy</Link> and{' '}
                                        <Link href="/terms-of-service" target="_blank">terms of service</Link> for more details.
                                    </Typography>
                                </Collapse>
                            </Box>
                        </Box>
                    </form>
                </Paper>
            </Container>

            <Footer />

            {/* Modal */}
            <Dialog open={modalVisible} onClose={() => { }} maxWidth="xs" fullWidth>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6">{modalMessage}</Typography>
                    {!showRetry && (
                        <Box mt={3}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                {showRetry && (
                    <DialogActions>
                        <Button onClick={handleRetry} variant="outlined" fullWidth>
                            Retry
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </Box>
    );
};

export default Login;
